/* eslint-disable no-param-reassign */
import { cloneDeep } from "lodash-es";
import { FC, useEffect, useMemo } from "react";

import { OutcomeGroupBox } from "./OutcomeGroupBox";
import { ProgressOverviewBox } from "./ProgressOverviewBox";

import { useAIContext } from "components/ai/hooks/use-ai-context";
import { getStudentDemoPrompt } from "components/materials/home/admin/settings/StudentDemoAIPrompt";
import { STUDENT_DEMO_OUTCOME_DATA } from "components/outcomes/FakeStudentOtherCourseOutcomeUseIndex";
import { storeApi, useAppSelector } from "store/index";
import { NestedOutcomeGroupType, selectNestedOutcomeGroups } from "store/selectors";
import { sortById } from "utils/collections";
import { averageAndRound } from "utils/math";

const getFakeOutcomeScores = () => {
  const outcomeScores = STUDENT_DEMO_OUTCOME_DATA.reduce(
    (acc, outcome_score) => {
      if (typeof outcome_score.score === "number") {
        if (!acc[outcome_score.learningOutcome]) {
          acc[outcome_score.learningOutcome] = [];
        }

        acc[outcome_score.learningOutcome].push(outcome_score.score);
      }

      return acc;
    },
    {} as Record<string, number[]>,
  );

  const averageScores = Object.keys(outcomeScores).reduce(
    (acc, outcomeName) => {
      const scores = outcomeScores[outcomeName];
      acc[outcomeName] = averageAndRound(scores);
      return acc;
    },
    {} as Record<string, number>,
  );

  return averageScores;
};

const nextCounter = (counter: number, length: number) => (counter >= length - 1 ? 0 : counter + 1);

const mapScoresToOutcomeGroups = (
  outcomeGroups: NestedOutcomeGroupType[],
  outcomeScores: Record<string, number>,
) => {
  const scoredOutcomeGroups = cloneDeep(
    outcomeGroups.filter(
      (group) =>
        group.outcomes.length > 0 ||
        group.outcomeSubgroups.some((subgroup) => subgroup.outcomes.length > 0),
    ),
  );
  const outcomeNames = Object.keys(outcomeScores);
  let outcomeCounter = 0;
  const totalScores: number[] = [];

  scoredOutcomeGroups.sort(sortById).forEach((group) => {
    const groupScores: number[] = [];

    group.outcomes.sort(sortById).forEach((outcome) => {
      const score = outcomeScores[outcomeNames[outcomeCounter]] || 0;

      outcome.averageScore = score;
      groupScores.push(score);
      totalScores.push(score);

      outcomeCounter = nextCounter(outcomeCounter, outcomeNames.length);
    });

    group.outcomeSubgroups.sort(sortById).forEach((subgroup) => {
      const subgroupScores: number[] = [];

      subgroup.outcomes.sort(sortById).forEach((outcome) => {
        const score = outcomeScores[outcomeNames[outcomeCounter]] || 0;

        outcome.averageScore = score;
        subgroupScores.push(score);
        groupScores.push(score);
        totalScores.push(score);

        outcomeCounter = nextCounter(outcomeCounter, outcomeNames.length);
      });

      subgroup.averageOutcomeScore = averageAndRound(subgroupScores);
    });

    group.averageOutcomeScore = averageAndRound(groupScores);
  });

  const totalScore = averageAndRound(totalScores);

  return { totalScore, scoredOutcomeGroups };
};

export const FakeProgressOverview: FC<{ withOutcomeCards?: boolean }> = ({
  withOutcomeCards = false,
}) => {
  const outcomeGroups = useAppSelector(selectNestedOutcomeGroups);
  // TODO: get all outcome scores for the current user
  const outcomeScores = getFakeOutcomeScores();
  const { totalScore, scoredOutcomeGroups } = mapScoresToOutcomeGroups(
    outcomeGroups,
    outcomeScores,
  );

  useEffect(() => {
    storeApi.outcomes.list();
    storeApi.outcome_subgroups.list();
    storeApi.outcome_groups.list();

    // TODO: Load these (and they should not be course scoped) when we skip fake data.
    //   storeApi.course_outcomes.list();
    //   storeApi.page_outcomes.list();
    //   storeApi.submissions.list();
    //   storeApi.assessments.list();
    //   storeApi.outcome_assessments.list();
  }, []);

  const demoData = useMemo(
    () =>
      [
        getStudentDemoPrompt(),
        "Here are my academic outcomes so far:",
        JSON.stringify(outcomeGroups),
      ].join("\n\n"),
    [outcomeGroups],
  );

  useAIContext("course_progress_demo", demoData, "user");

  const outcomeCards = scoredOutcomeGroups.map((outcomeGroup) => {
    return <OutcomeGroupBox key={outcomeGroup.id} outcomeGroup={outcomeGroup} />;
  });

  return (
    <div className="flex h-full w-full flex-col gap-6 self-stretch overflow-y-auto">
      <ProgressOverviewBox
        outcomeGroups={scoredOutcomeGroups}
        totalAssessed={150}
        totalScore={totalScore}
      />

      {withOutcomeCards && outcomeCards}
    </div>
  );
};
